import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, Loader, Home } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, []);

  const pollPaymentStatus = async (sessionId, currentAttempt = 0) => {
    if (currentAttempt >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentDetails(response.data);
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setAttempts(currentAttempt + 1);
      setTimeout(() => pollPaymentStatus(sessionId, currentAttempt + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      
      // Retry on error
      if (currentAttempt < maxAttempts) {
        setAttempts(currentAttempt + 1);
        setTimeout(() => pollPaymentStatus(sessionId, currentAttempt + 1), 2000);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8">
          {status === 'checking' && (
            <div className="text-center">
              <Loader className="w-16 h-16 mx-auto mb-4 animate-spin text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Vérification du paiement...
              </h2>
              <p className="text-slate-600 mb-4">
                Veuillez patienter pendant que nous confirmons votre paiement.
              </p>
              <div className="text-sm text-slate-500">
                Tentative {attempts + 1}/{maxAttempts}
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Paiement Réussi !
              </h2>
              <p className="text-slate-600 mb-6">
                Merci ! Votre paiement a été confirmé avec succès.
              </p>
              
              {paymentDetails && (
                <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-600">Montant :</span>
                    <span className="font-bold text-green-600">
                      {paymentDetails.amount?.toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-slate-600">Transaction :</span>
                    <span className="text-sm font-mono text-slate-900">
                      {paymentDetails.payment_id?.substring(0, 8)}...
                    </span>
                  </div>
                </div>
              )}

              <p className="text-sm text-slate-500 mb-6">
                Un email de confirmation a été envoyé à votre adresse.
              </p>

              <Button
                onClick={() => navigate('/')}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                <Home className="w-4 h-4 mr-2" />
                Retour à l'accueil
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Erreur de vérification
              </h2>
              <p className="text-slate-600 mb-6">
                Nous n'avons pas pu vérifier votre paiement. Veuillez vérifier vos emails ou nous contacter.
              </p>
              
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full"
              >
                <Home className="w-4 h-4 mr-2" />
                Retour à l'accueil
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
